import React from "react";
import {
  Box,
  Typography,
  IconButton,
  Fade,
  Slide,
  Paper,
  Chip,
  Button,
  CircularProgress,
} from "@mui/material";
import {
  ErrorOutline,
  Close,
  WifiOff,
  Refresh,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { setWebsocketDisconnected, setWebsocketManualReconnect } from "../../Redux/Reducers/uiSlice";
import { selectWebsocketManualReconnect } from "../../Redux/Reducers/selectors";
import { useWebSocketStatus } from "../../hooks/useWebSocket";

export const WebsocketDisconnectionNotification = ({ message = "Connection Lost", onManualReconnect }) => {
  const dispatch = useDispatch();
  const isManualReconnect = useSelector(selectWebsocketManualReconnect);
  const { isReconnecting, reconnectAttempts, maxReconnectAttempts } = useWebSocketStatus();

  const handleDismiss = () => {
    dispatch(setWebsocketDisconnected(false));
    dispatch(setWebsocketManualReconnect(false));
  };

  const handleReconnect = async () => {
    if (onManualReconnect) {
      // Start manual reconnection - notification will be hidden automatically on success
      // through the existing connect event handler
      onManualReconnect();
    }
  };

  return (
    <Fade in={true} timeout={300}>
      <Slide direction="up" in={true} timeout={300}>
        <Box
          sx={{
            position: "fixed",
            bottom: 24,
            zoom: 0.75,
            right: 24,
            zIndex: 9999,
            maxWidth: 400,
            minWidth: 320,
          }}
        >
          <Paper
            elevation={8}
            sx={{
              borderRadius: "12px",
              overflow: "hidden",
              backgroundColor: "#d32f2f", // Red background
              border: "1px solid rgba(255, 255, 255, 0.2)",
              backdropFilter: "blur(10px)",
            }}
          >
            {/* Header with icon and close button */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "16px 20px 12px 20px",
                borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 32,
                    height: 32,
                    borderRadius: "8px",
                    backgroundColor: "rgba(255, 255, 255, 0.15)",
                    color: "white",
                  }}
                >
                  <WifiOff sx={{ fontSize: "18px" }} />
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: "16px",
                    fontWeight: 700,
                    color: "white",
                    lineHeight: 1.2,
                  }}
                >
                  {isManualReconnect ? "Reconnection Failed" : "Connection Error"}
                </Typography>
              </Box>
            </Box>

            {/* Content */}
            <Box sx={{ padding: "16px 20px 20px 20px" }}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 24,
                    height: 24,
                    borderRadius: "6px",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    color: "white",
                    flexShrink: 0,
                    marginTop: "2px",
                  }}
                >
                  {isReconnecting ? (
                    <CircularProgress size={14} sx={{ color: "white" }} />
                  ) : (
                    <ErrorOutline sx={{ fontSize: "14px" }} />
                  )}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      lineHeight: 1.3,
                      mb: 0.5,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      color: "white",
                    }}
                  >
                    {isManualReconnect ? "Cannot reconnect automatically" : message}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "11px",
                      opacity: 0.8,
                      lineHeight: 1.2,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      color: "white",
                      mb: isManualReconnect ? (isReconnecting ? 1 : 1.5) : (isReconnecting ? 0.5 : 0),
                    }}
                  >
                    {isManualReconnect
                      ? "Click the button below to try reconnecting manually"
                      : "Attempting to reconnect automatically..."}
                  </Typography>
                  {reconnectAttempts !== maxReconnectAttempts && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                     
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "12px",
                          opacity: 0.7,
                          lineHeight: 1.2,
                          color: "white",
                        }}
                      >
                        Reconnecting... ({reconnectAttempts}/{maxReconnectAttempts})
                      </Typography>
                      <CircularProgress size={12} sx={{ color: "white", opacity: 0.8 }} />
                    </Box>
                  )}
                  {isManualReconnect && (
                    <Button
                      variant="contained"
                      startIcon={<Refresh />}
                      onClick={handleReconnect}
                      sx={{
                        backgroundColor: "rgba(255, 255, 255, 0.2)",
                        color: "white",
                        '&:hover': {
                          backgroundColor: "rgba(255, 255, 255, 0.3)",
                        },
                        fontSize: "12px",
                        padding: "6px 16px",
                        minWidth: "auto",
                        mt: 2
                      }}
                    >
                      Reconnect
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>

            {/* Progress bar */}
            <Box
              sx={{
                height: 3,
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  width: "100%",
                  backgroundColor: "rgba(255, 255, 255, 0.3)",
                  animation: "pulse 2s ease-in-out infinite",
                  "@keyframes pulse": {
                    "0%": {
                      opacity: 0.3,
                    },
                    "50%": {
                      opacity: 0.7,
                    },
                    "100%": {
                      opacity: 0.3,
                    },
                  },
                }}
              />
            </Box>
          </Paper>
        </Box>
      </Slide>
    </Fade>
  );
};
